import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { AgGridReact } from 'ag-grid-react'
import { refreshQueueNow } from '../../../shared/realtime/queueRefresh'
import type { Request, RequestItem } from '../../../shared/api/types'
import { recordProcurementDownload } from '../../../shared/api/requests.api'
import { setProcurementState } from '../../../shared/api/requestItems.api'
import { generateProcurementRequest } from '../utils/generateProcurementRequest'
import {
  bulkCreateProcurementRequests,
  type BulkSupplierInput,
} from '../../../shared/api/procurementRequests.api'
import { SupplierSelectModal } from '../../procurement/components/SupplierSelectModal'
import { buildManufacturerReportText } from '../../procurement/queueActions'
import { toast } from '../../../shared/ui/toastStore'
import { useAuthStore } from '../../auth/authStore'
import { useBulkAssignProcurement } from '../hooks/useBulkAssignProcurement'
import { AssignSelectedControl } from './AssignSelectedControl'

type Props = {
  requestId: string
  requestNumber: string
  clientCode: string | null
  selectedItems: RequestItem[]
  onClear: () => void
}

/**
 * Закупщицкая панель действий над выделенными позициями. Появляется
 * для PROCUREMENT и ROS, когда хотя бы одна позиция выделена в гриде
 * (чекбоксы в колонке `select`). Две операции:
 *
 *  • «Скачать запрос (N)» — Excel только по выделенным (как «Сформировать
 *    запрос» в очереди квот, но в рамках одной заявки).
 *  • «Поставщик не ответил (N)» — массово ставит личный закупщицкий статус
 *    NO_RESPONSE. Глобальный статус позиции при этом не трогаем.
 *  • «Назначить…» — только у РОЗ (№50, 29.09.2026): на себя или закупщика
 *    своей команды, тем же bulk-assign, что и у продаж.
 */
export function ProcurementSelectedBar({
  requestId,
  requestNumber,
  clientCode,
  selectedItems,
  onClear,
}: Props) {
  const queryClient = useQueryClient()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isMarking, setIsMarking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [supplierModalOpen, setSupplierModalOpen] = useState(false)
  const count = selectedItems.length
  const isRos = useAuthStore((s) => s.user?.role) === 'ROS'
  // Выделение в гриде снимает onClear (cancelSelection), ref хуку не нужен.
  const noGrid = useRef<AgGridReact | null>(null)
  const assign = useBulkAssignProcurement(requestId, noGrid, onClear)

  // Открыть модалку выбора поставщиков. Дальше handleConfirmSuppliers создаёт
  // ProcurementRequest на каждого поставщика, скачивает файлы и обновляет
  // closely-related queries.
  const handleDownload = () => {
    if (count === 0 || isDownloading) return
    setError(null)
    setSupplierModalOpen(true)
  }

  const handleConfirmSuppliers = async (
    suppliers: BulkSupplierInput[],
    note: string | null,
    manufacturer: string | null,
  ) => {
    if (count === 0) return
    setIsDownloading(true)
    try {
      const ids = selectedItems.map((i) => i.id)
      const itemById = new Map(selectedItems.map((i) => [i.id, i]))
      const { requests: created, manufacturerReport } = await bulkCreateProcurementRequests({
        requestItemIds: ids,
        suppliers,
        manufacturer: manufacturer ? { name: manufacturer } : undefined,
        note: note ?? undefined,
      })
      for (const req of created) {
        // BOM производителю — только вошедшие позиции (занятые исключены).
        const bomItems = req.items
          .map((bi) => (bi.requestItemId ? itemById.get(bi.requestItemId) : undefined))
          .filter((i): i is (typeof selectedItems)[number] => !!i)
        await generateProcurementRequest({
          clientCode,
          requestNumber: req.fileCode,
          isBatch: true,
          supplierName: req.supplierName,
          fileCode: req.fileCode,
          items: bomItems.map((item) => ({
            requestItemId: item.id,
            clientCode,
            requestNumber,
            partNumber: item.partNumber,
            brand: item.brand,
            quantity: item.quantity,
            comment: item.comment,
          })),
        })
      }
      const reportText = buildManufacturerReportText(manufacturerReport)
      if (reportText) toast.info(reportText)
      // Старая логика: per-user state=REQUESTED для совместимости с
      // procurementStatus заявки. Трогаем только позиции, реально вошедшие
      // хоть в один BOM (раскладка/занятые производителем остаются «Новая»).
      const sentIds = new Set(
        created.flatMap((r) => r.items.map((it) => it.requestItemId)).filter(Boolean),
      )
      const touchedIds = ids.filter((id) => sentIds.has(id))
      try {
        if (touchedIds.length > 0) await recordProcurementDownload(requestId, touchedIds)
      } catch {
        /* не блокируем при ошибке фиксации */
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requests', requestId] }),
        refreshQueueNow(),
        queryClient.invalidateQueries({ queryKey: ['procurement-requests'] }),
        ...ids.map((itemId) =>
          queryClient.invalidateQueries({ queryKey: ['item-proc-state', itemId] }),
        ),
      ])
      setSupplierModalOpen(false)
    } catch (e) {
      setError((e as Error).message ?? 'Не удалось сформировать запрос')
    } finally {
      setIsDownloading(false)
    }
  }

  // Скачать Excel «без поставщика»: файл «ноунейм» из выбранных позиций,
  // без создания ProcurementRequest и без смены статусов позиций.
  const handleDownloadNoSupplier = async () => {
    if (count === 0) return
    await generateProcurementRequest({
      clientCode,
      requestNumber: 'ноунейм',
      isBatch: true,
      fileNameOverride: 'ноунейм',
      items: selectedItems.map((item) => ({
        requestItemId: item.id,
        clientCode,
        requestNumber,
        partNumber: item.partNumber,
        brand: item.brand,
        quantity: item.quantity,
        comment: item.comment,
      })),
    })
    setSupplierModalOpen(false)
  }

  const handleMarkNoResponse = async () => {
    if (count === 0 || isMarking) return
    setError(null)
    setIsMarking(true)
    const ids = selectedItems.map((i) => i.id)
    try {
      const results = await Promise.allSettled(
        ids.map((itemId) => setProcurementState(itemId, 'NO_RESPONSE')),
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) {
        setError(`Не удалось обновить ${failed} из ${count}`)
      }
      if (failed === 0) {
        queryClient.setQueryData<Request | undefined>(['requests', requestId], (old) => {
          if (!old?.items) return old
          const idSet = new Set(ids)
          return {
            ...old,
            items: old.items.map((item) =>
              idSet.has(item.id) ? { ...item, procurementState: 'NO_RESPONSE' } : item,
            ),
          }
        })
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requests', requestId] }),
        queryClient.invalidateQueries({ queryKey: ['requests'] }),
        refreshQueueNow(),
        ...ids.map((itemId) =>
          queryClient.invalidateQueries({ queryKey: ['item-proc-state', itemId] }),
        ),
      ])
      if (failed === 0) onClear()
    } catch (e) {
      setError((e as Error).message ?? 'Не удалось обновить выбранные позиции')
    } finally {
      setIsMarking(false)
    }
  }

  const isBusy = isDownloading || isMarking || assign.isApplying

  return (
    <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-2 bg-gray-50/60 flex-wrap">
      <span className="text-xs text-gray-500 mr-1">Выбрано: {count}</span>

      <button
        onClick={handleDownload}
        disabled={isBusy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        {isDownloading ? 'Формирование…' : `Скачать запрос (${count})`}
      </button>

      <button
        onClick={handleMarkNoResponse}
        disabled={isBusy}
        className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
      >
        {isMarking ? 'Применение…' : `Поставщик не ответил (${count})`}
      </button>

      {isRos && (
        <AssignSelectedControl
          placeholder="Назначить…"
          isAssigning={assign.isApplying}
          onAssign={(assignedToId) =>
            void assign.apply(
              selectedItems.map((i) => i.id),
              assignedToId,
            )
          }
        />
      )}

      <button
        onClick={() => {
          assign.clearError()
          onClear()
        }}
        disabled={isBusy}
        className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
      >
        Отменить
      </button>

      {(error || assign.error) && (
        <span className="text-xs text-red-600 basis-full mt-1">{error || assign.error}</span>
      )}

      {supplierModalOpen && (
        <SupplierSelectModal
          itemsCount={count}
          onClose={() => setSupplierModalOpen(false)}
          onConfirm={handleConfirmSuppliers}
          onDownloadNoSupplier={handleDownloadNoSupplier}
        />
      )}
    </div>
  )
}
